import { getAuthenticatedUser } from '@ctw/shared/api/auth-service/user';
import { usePractitioner } from '@ctw/shared/api/fhir/practitioner';
import { useCTW } from '@ctw/shared/context/ctw-context';
import { useCtwQuery } from '@ctw/shared/hooks/use-ctw-query';
import { QUERY_KEY_AUTHENTICATED_USER_BY_ID } from '@ctw/shared/utils/query-keys';
import {
  type AuthenticatedUserState,
  type AuthenticatedZusUser,
  buildAuthenticatedUserState,
} from '@ctw/shared/utils/zus-authenticated-user';
import { isEmpty } from 'lodash-es';
import { type PropsWithChildren, createContext, useContext, useMemo } from 'react';

interface AuthenticatedUserLoading {
  status: 'loading';
}

interface AuthenticatedUserError {
  status: 'error';
  error: Error;
}

interface AuthenticatedUserSuccess {
  status: 'success';
  data: AuthenticatedUserState;
}

export type AuthenticatedUserResult =
  | AuthenticatedUserLoading
  | AuthenticatedUserError
  | AuthenticatedUserSuccess;

const AuthenticatedUserContext = createContext<AuthenticatedUserResult | null>(null);

interface AuthenticatedUserProviderProps extends PropsWithChildren {}

export const AuthenticatedUserProvider = ({ children }: AuthenticatedUserProviderProps) => {
  const { requestContext } = useCTW();
  const authenticatedUserQuery = useCtwQuery({
    queryId: QUERY_KEY_AUTHENTICATED_USER_BY_ID,
    queryKey: [requestContext.authTokenState.zusUserId],
    queryFn: async ({ telemetry, requestContext }): Promise<AuthenticatedZusUser | null> => {
      if (isEmpty(requestContext.authTokenState.zusUserId)) {
        return null;
      }

      const authenticatedUserResponse = await getAuthenticatedUser({
        env: requestContext.env,
        accessToken: requestContext.authToken,
        telemetry,
      });

      return authenticatedUserResponse
        ? {
            id: authenticatedUserResponse.data.id,
            ...authenticatedUserResponse.data.attributes,
          }
        : null;
    },
  });
  const practitionerQuery = usePractitioner({
    practitionerId: requestContext.authTokenState.practitionerId,
  });

  const result: AuthenticatedUserResult = useMemo(() => {
    if (authenticatedUserQuery.isLoading || practitionerQuery.isLoading) {
      return { status: 'loading' };
    }

    if (authenticatedUserQuery.isError) {
      return { status: 'error', error: authenticatedUserQuery.error };
    }

    if (practitionerQuery.isError) {
      return { status: 'error', error: practitionerQuery.error };
    }

    return {
      status: 'success',
      data: buildAuthenticatedUserState({
        authTokenState: requestContext.authTokenState,
        authenticatedUser: authenticatedUserQuery.data ?? null,
        practitioner: practitionerQuery.data ?? null,
      }),
    };
  }, [requestContext, authenticatedUserQuery, practitionerQuery]);

  return (
    <AuthenticatedUserContext.Provider value={result}>{children}</AuthenticatedUserContext.Provider>
  );
};

export const useAuthenticatedUser = (): AuthenticatedUserResult => {
  const context = useContext(AuthenticatedUserContext);

  if (!context) {
    throw new Error('useAuthenticatedUser must be used within a AuthenticatedUserProvider');
  }

  return context;
};
